import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Divider } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import ScopeDropdownModal, {
    ScopePickerConfig,
    ScopePickerFilterConfig,
} from 'components/molecules/Generic/ScopeDropdownModal';
import { colorConstants } from 'config/colorConstants';
import { FilterContextData } from 'contexts/FilterContext';
import {
    applyFilterMutation,
    getScopeOptionsQuery,
    getUserScopeQuery,
    saveUserScopeMutation,
} from 'gqlQueries/filterQueries';
import { sendNotification } from 'utils/helperMethods';
import { hasPpaScopeFilterIds, parseScopeDimToSelection } from 'hooks/ppa/ppaScope.helpers';
import {
    PPA_SCOPE_EDIT_BTN,
    PPA_SCOPE_RIBBON,
    PPA_SCOPE_TEST_ID_PREFIX,
    ppaScopeRibbonValueTestId,
} from 'constants/ppaTestIds';

const SUPPORTED_SCOPE_DIMENSIONS = ['dim1', 'dim2', 'dim3', 'dim4'] as const;

const buildInclusionPayload = (filter: ScopePickerFilterConfig, selectedValue?: string) => {
    if (!selectedValue) {
        return null;
    }
    return JSON.stringify({
        inclusion: [
            {
                dimension: filter.dimension,
                isActive: true,
                attributeType: filter.attribute_type,
                attributeDesc: selectedValue,
                attributeExId: selectedValue,
                isAttribute: filter.is_attribute,
            },
        ],
    });
};

const EditScopeIconButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.50421 1.33745C10.3763 0.465315 11.7904 0.465316 12.6625 1.33745C13.5346 2.20959 13.5346 3.62361 12.6625 4.49575L10.914 6.24421C10.9135 6.24472 10.913 6.24524 10.9125 6.24575C10.912 6.24626 10.9115 6.24677 10.911 6.24727L4.95882 12.1994C4.94904 12.2092 4.93936 12.2189 4.92974 12.2285C4.78689 12.3716 4.66087 12.4978 4.51115 12.5965C4.37952 12.6834 4.23675 12.752 4.08674 12.8005C3.91611 12.8558 3.73883 12.8753 3.53785 12.8975C3.52433 12.899 3.51071 12.9005 3.49697 12.902L1.52276 13.1214C1.34663 13.1409 1.17116 13.0794 1.04586 12.9541C0.920554 12.8288 0.859002 12.6533 0.878572 12.4772L1.09793 10.503C1.09946 10.4892 1.10096 10.4756 1.10245 10.4621C1.12462 10.2611 1.14417 10.0838 1.19941 9.9132C1.24797 9.76318 1.31659 9.62042 1.40339 9.48878C1.50212 9.33906 1.62834 9.21305 1.77142 9.07019C1.78105 9.06058 1.79076 9.05089 1.80053 9.04111L9.50421 1.33745ZM8.16668 4.32489L2.62549 9.86607C2.43736 10.0542 2.40244 10.093 2.37736 10.131C2.34842 10.1749 2.32555 10.2225 2.30936 10.2725C2.29533 10.3159 2.28684 10.3674 2.25746 10.6318L2.11863 11.8813L3.36813 11.7425C3.63256 11.7131 3.68408 11.7046 3.72742 11.6906C3.77743 11.6744 3.82501 11.6515 3.86889 11.6226C3.90692 11.5975 3.94574 11.5626 4.13387 11.3744L9.67506 5.83326L8.16668 4.32489ZM10.5 5.00831L8.99164 3.49993L10.3292 2.16241C10.7457 1.74588 11.421 1.74588 11.8375 2.16241C12.2541 2.57894 12.2541 3.25426 11.8375 3.67079L10.5 5.00831Z" fill="black" fill-opacity="0.25" />
    </svg>
)


type ScopeRibbonProps = {
    scopeConfig?: ScopePickerConfig;
};

const buildScopeSelectionsFromPersistedScope = (
    filters: ScopePickerFilterConfig[],
    persistedScope?: Record<string, unknown> | null,
) => {
    if (!persistedScope) {
        return {};
    }

    return filters.reduce<Record<string, string>>((accumulator, filter) => {
        const savedSelection = parseScopeDimToSelection(persistedScope[filter.dimension]);
        if (savedSelection) {
            accumulator[filter.filter_id] = savedSelection;
        }
        return accumulator;
    }, {});
};

const getStoredUserEmail = () => {
    try {
        return localStorage.getItem('email') || '';
    } catch {
        return '';
    }
};

const ScopeRibbon = ({ scopeConfig }: ScopeRibbonProps) => {
    const {
        ppaScopeFilterIds,
        setPpaScopeFilterIds,
        setPpaScopeFilterLoading,
        setPpaGlobalFilterApplied,
        ppaPersistedScope,
        setPpaPersistedScope,
    } = useContext(FilterContextData);
    const [scopeModalVisible, setScopeModalVisible] = useState(false);
    const [selectedScopeValues, setSelectedScopeValues] = useState<Record<string, string>>({});
    const [scopeOptionsRows, setScopeOptionsRows] = useState<Record<string, any>[]>([]);
    const [isInitialScopeSelectionRequired, setIsInitialScopeSelectionRequired] = useState(false);
    const hasFetchedUserScopeRef = useRef(false);
    const scopeConfigKeyRef = useRef<string | null>(null);

    const filters = useMemo(
        () => [...(scopeConfig?.filters ?? [])].sort((a, b) => a.order_rank - b.order_rank),
        [scopeConfig?.filters],
    );
    const requiredFilters = useMemo(() => filters.filter((filter) => filter.is_required), [filters]);

    const userEmail = getStoredUserEmail();
    const scopeConfigKey = scopeConfig?.feature_name ?? 'default';

    const markScopeFilterReady = () => {
        setPpaScopeFilterLoading(false);
    };

    const markScopeFilterPending = () => {
        setPpaScopeFilterLoading(true);
    };

    const [resolveScopeSelections, { loading: applyScopeLoading }] = useMutation(applyFilterMutation, {
        onCompleted: (response) => {
            setPpaScopeFilterIds(response);
            setPpaGlobalFilterApplied(false);
            markScopeFilterReady();
        },
        onError: () => {
            markScopeFilterReady();
        },
    });

    const [saveScopeSelection] = useMutation(saveUserScopeMutation, {
        refetchQueries: userEmail
            ? [
                {
                    query: getUserScopeQuery,
                    variables: { userId: userEmail },
                },
            ]
            : [],
        awaitRefetchQueries: true,
    });

    const [fetchScopeOptions, { loading: scopeOptionsLoading }] = useLazyQuery(getScopeOptionsQuery, {
        fetchPolicy: 'network-only',
        onCompleted: (response) => {
            setScopeOptionsRows(response?.getScopeOptions ?? []);
        },
        onError: () => {
            sendNotification('error', 'Failed to load scope options.');
        },
    });

    const replayPersistedScope = async (persistedScope: Record<string, any>) => {
        markScopeFilterPending();
        try {
            const dimensionPayloads: Record<string, string | null> = {};
            SUPPORTED_SCOPE_DIMENSIONS.forEach((dimension) => {
                dimensionPayloads[`jsonstring_${dimension}`] = persistedScope?.[dimension] || null;
            });
            await resolveScopeSelections({
                variables: {
                    ...dimensionPayloads,
                    featureName: scopeConfig?.feature_name || 'default',
                },
            });
        } catch (_error) {
            sendNotification('error', 'Failed to apply saved scope.');
            markScopeFilterReady();
        }
    };

    const [fetchUserScope] = useLazyQuery(getUserScopeQuery, {
        fetchPolicy: 'network-only',
        onCompleted: async (response) => {
            const persistedScope = response?.getUserScope?.userScope ?? response?.getUserScope;
            if (!persistedScope) {
                setIsInitialScopeSelectionRequired(true);
                setScopeModalVisible(true);
                markScopeFilterReady();
                return;
            }

            const nextSelections = buildScopeSelectionsFromPersistedScope(filters, persistedScope);

            const hasAllRequiredSelections =
                requiredFilters.length > 0 &&
                requiredFilters.every((filter) => Boolean(nextSelections[filter.filter_id]));

            if (!hasAllRequiredSelections) {
                setSelectedScopeValues(nextSelections);
                setIsInitialScopeSelectionRequired(true);
                setScopeModalVisible(true);
                markScopeFilterReady();
                return;
            }

            setSelectedScopeValues(nextSelections);
            setIsInitialScopeSelectionRequired(false);
            setPpaPersistedScope(persistedScope);

            if (hasPpaScopeFilterIds(ppaScopeFilterIds)) {
                markScopeFilterReady();
                return;
            }

            await replayPersistedScope(persistedScope);
        },
        onError: () => {
            setIsInitialScopeSelectionRequired(true);
            setScopeModalVisible(true);
            markScopeFilterReady();
        },
    });

    useLayoutEffect(() => {
        if (scopeConfig?.filters?.length) {
            markScopeFilterPending();
        }
    }, [scopeConfig?.filters?.length, setPpaScopeFilterLoading]);

    useEffect(() => {
        if (!scopeConfig || !filters.length) {
            markScopeFilterReady();
            return;
        }

        if (scopeConfigKeyRef.current !== scopeConfigKey) {
            scopeConfigKeyRef.current = scopeConfigKey;
            hasFetchedUserScopeRef.current = false;
        }

        markScopeFilterPending();

        if (!userEmail) {
            setIsInitialScopeSelectionRequired(true);
            setScopeModalVisible(true);
            markScopeFilterReady();
            return;
        }

        if (hasFetchedUserScopeRef.current) {
            markScopeFilterReady();
            return;
        }

        hasFetchedUserScopeRef.current = true;
        fetchUserScope({ variables: { userId: userEmail } });
    }, [scopeConfig, scopeConfigKey, userEmail, filters.length, fetchUserScope, setPpaScopeFilterLoading]);

    useEffect(() => {
        return () => {
            setPpaScopeFilterLoading(false);
        };
    }, [setPpaScopeFilterLoading]);

    useEffect(() => {
        if (!filters.length || Object.keys(selectedScopeValues).length > 0) {
            return;
        }

        const persistedSelections = buildScopeSelectionsFromPersistedScope(filters, ppaPersistedScope);
        if (Object.keys(persistedSelections).length > 0) {
            setSelectedScopeValues(persistedSelections);
        }
    }, [filters, ppaPersistedScope, selectedScopeValues]);

    useEffect(() => {
        if (!scopeModalVisible || scopeOptionsRows.length > 0) {
            return;
        }
        fetchScopeOptions();
    }, [scopeModalVisible, scopeOptionsRows.length, fetchScopeOptions]);

    const handleOpenScopeEditor = () => {
        setScopeModalVisible(true);
        if (scopeOptionsRows.length === 0) {
            fetchScopeOptions();
        }
    };

    const handleScopeApply = async (values: Record<string, string>) => {
        if (!requiredFilters.length) {
            return;
        }

        const missingRequiredFilter = requiredFilters.find((filter) => !values[filter.filter_id]);
        if (missingRequiredFilter) {
            sendNotification('error', `Please select ${missingRequiredFilter.label.toLowerCase()} to continue.`);
            return;
        }

        const payloadByDimension = filters.reduce<Record<string, string | null>>((accumulator, filter) => {
            accumulator[filter.dimension] = buildInclusionPayload(filter, values[filter.filter_id]);
            return accumulator;
        }, {});

        try {
            markScopeFilterPending();
            const dimensionPayloads: Record<string, string | null> = {};
            SUPPORTED_SCOPE_DIMENSIONS.forEach((dimension) => {
                dimensionPayloads[`jsonstring_${dimension}`] = payloadByDimension[dimension] || null;
            });
            await resolveScopeSelections({
                variables: {
                    ...dimensionPayloads,
                    featureName: scopeConfig?.feature_name || 'default',
                },
            });
            if (userEmail) {
                await saveScopeSelection({
                    variables: {
                        userEmail,
                        dim1: payloadByDimension.dim1 || null,
                        dim2: payloadByDimension.dim2 || null,
                    },
                });
            }
            setSelectedScopeValues(values);
            setPpaPersistedScope(payloadByDimension);
            setIsInitialScopeSelectionRequired(false);
            setScopeModalVisible(false);
        } catch (_error) {
            sendNotification('error', 'Unable to save your scope. Please try again.');
            markScopeFilterReady();
        }
    };

    const resolvedScopeValues = useMemo(() => {
        if (Object.keys(selectedScopeValues).length > 0) {
            return selectedScopeValues;
        }
        return buildScopeSelectionsFromPersistedScope(filters, ppaPersistedScope);
    }, [filters, ppaPersistedScope, selectedScopeValues]);

    const selectedScopeEntries = filters
        .map((filter) => ({
            filterId: filter.filter_id,
            value: resolvedScopeValues[filter.filter_id],
        }))
        .filter((entry) => Boolean(entry.value));
    const showScopeValue = selectedScopeEntries.length > 0;
    const isScopePickerEnabled = filters.length > 0;

    return (
        <>
            <div
                className="flex items-center justify-between rounded-md bg-blackAlt/5 px-4 py-2"
                data-testid={PPA_SCOPE_RIBBON}
            >
                <div className="flex items-center gap-2 text-sm leading-[22px] text-blackTitle">
                    <span>PPA Scope:</span>
                    <div className="flex items-center gap-2 font-medium">
                        {showScopeValue ? (
                            <>
                                {selectedScopeEntries.map((entry, index) => (
                                    <div key={entry.filterId} className="flex items-center gap-2">
                                        {index > 0 ? (
                                            <Divider type="vertical" className="!mx-0 !h-4 border-[#d4d4d8]" />
                                        ) : null}
                                        <span data-testid={ppaScopeRibbonValueTestId(entry.filterId)}>
                                            {entry.value}
                                        </span>
                                    </div>
                                ))}
                                {!selectedScopeEntries.length ? (
                                    <>
                                        <span>-</span>
                                        <Divider type="vertical" className="!mx-0 !h-4 border-[#d4d4d8]" />
                                        <span>-</span>
                                    </>
                                ) : null}
                            </>
                        ) : (
                            <span className="text-fontGreyAlt">Not selected</span>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleOpenScopeEditor}
                    disabled={!isScopePickerEnabled}
                    className="flex items-center gap-2 rounded-sm px-[7px] text-sm leading-[22px] text-[rgba(0,0,0,0.25)] disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid={PPA_SCOPE_EDIT_BTN}
                >
                    <EditScopeIconButton />
                    <span className="font-[500] text-fontGreyAlt">Edit Scope</span>
                </button>
            </div>
            <ScopeDropdownModal
                open={scopeModalVisible}
                onClose={() => {
                    if (!isInitialScopeSelectionRequired) {
                        setScopeModalVisible(false);
                    }
                }}
                onApply={handleScopeApply}
                scopeConfig={scopeConfig}
                optionsRows={scopeOptionsRows}
                selectedValues={resolvedScopeValues}
                applyLoading={applyScopeLoading || scopeOptionsLoading}
                isMandatorySelection={isInitialScopeSelectionRequired}
                testIdPrefix={PPA_SCOPE_TEST_ID_PREFIX}
            />
        </>
    );
};

export default ScopeRibbon;
